import { Brain, Gauge, Zap, Globe, FileText, Target, Activity, Smartphone, Shield, MapPin, Truck, BarChart3 } from 'lucide-react';
import React from 'react';

// Diagnose Features
export const diagnoseFeatures = {
  'dual-ai': {
    icon: <Brain className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Dual-AI-System',
    subtitle: 'Gemini 2.5 Pro + GPT-4o validieren sich gegenseitig',
    description: 'Unser revolutionäres Dual-AI-System kombiniert die Stärken von Google Gemini 2.5 Pro und OpenAI GPT-4o. Beide KI-Modelle analysieren Ihr Fahrzeugproblem unabhängig und validieren ihre Ergebnisse gegenseitig für maximale Genauigkeit und Zuverlässigkeit.',
    keyFeatures: [
      {
        title: 'Gemini 2.5 Pro Integration',
        description: 'Nutzt Googles neuestes Sprachmodell für präzise technische Analyse'
      },
      {
        title: 'GPT-4o Cross-Validation',
        description: 'OpenAIs fortschrittlichste KI überprüft und ergänzt die Diagnose'
      },
      {
        title: 'Konsensbasierte Ergebnisse',
        description: 'Nur Diagnosen, bei denen beide KIs übereinstimmen, werden präsentiert'
      },
      {
        title: '99.5% Genauigkeit',
        description: 'Durch die Dual-Validierung erreichen wir eine Diagnosegen auigkeit von über 99.5%'
      }
    ],
    howItWorks: [
      {
        title: 'Problemerfassung',
        description: 'Sie beschreiben das Fahrzeugproblem in eigenen Worten'
      },
      {
        title: 'Parallele Analyse',
        description: 'Gemini und GPT-4o analysieren gleichzeitig und unabhängig'
      },
      {
        title: 'Kreuzvalidierung',
        description: 'Die Systeme vergleichen ihre Ergebnisse und prüfen auf Übereinstimmungen'
      },
      {
        title: 'Präzise Diagnose',
        description: 'Sie erhalten nur die validierten und bestätigten Diagnosen'
      }
    ],
    benefits: [
      'Höchste Diagnosegenauigkeit',
      'Minimierung von Fehldiagnosen',
      'Schnellere Problemlösung',
      'Kostenersparnis durch präzise Diagnosen'
    ],
    screenshots: [
      {
        url: '/screenshots/dashboard.png',
        caption: 'Dashboard-Übersicht: Schneller Zugriff auf alle Diagnose-Funktionen und letzte Sessions'
      },
      {
        url: '/screenshots/history-sessions.png',
        caption: 'Diagnose-Historie: Alle abgeschlossenen Diagnosen mit detaillierten Reports zum Download'
      }
    ]
  },
  
  'intelligent-assessment': {
    icon: <Gauge className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Intelligente Istwert-Beurteilung',
    subtitle: 'Die KI vergleicht Messwerte mit Sollwerten und erklärt Abweichungen',
    description: 'Messen Sie Spannung, Widerstand oder Druck – die KI vergleicht mit Sollwerten und erklärt automatisch, ob Ihre Messung im Toleranzbereich liegt oder ob eine Abweichung vorliegt.',
    keyFeatures: [
      {
        title: 'Automatischer Sollwert-Vergleich',
        description: 'KI kennt die Sollwerte für Ihr Fahrzeugmodell'
      },
      {
        title: 'Toleranzbereich-Analyse',
        description: 'Prüft, ob Messwerte innerhalb der erlaubten Abweichung liegen'
      },
      {
        title: 'Verständliche Erklärungen',
        description: 'Erklärt in einfachen Worten, was die Abweichung bedeutet'
      },
      {
        title: 'Multi-Unit-Support',
        description: 'Unterstützt Volt, Ampere, Ohm, Bar, ml/min und viele mehr'
      }
    ],
    howItWorks: [
      {
        title: 'Messwert eingeben',
        description: 'Geben Sie Ihren gemessenen Wert mit Einheit ein (z.B. 12.6V)'
      },
      {
        title: 'KI analysiert',
        description: 'System vergleicht mit Herstellervorgaben für Ihr Fahrzeug'
      },
      {
        title: 'Status erhalten',
        description: 'Grün = OK, Gelb = Grenzwertig, Rot = Außerhalb Toleranz'
      },
      {
        title: 'Empfehlung bekommen',
        description: 'KI gibt konkrete Handlungsempfehlungen'
      }
    ],
    benefits: [
      'Keine manuellen Sollwert-Recherchen',
      'Sofortige Beurteilung',
      'Fehler durch falsche Interpretation vermeiden',
      'Zeit und Kosten sparen'
    ],
    screenshots: [
      {
        url: '/screenshots/vehicles-list.png',
        caption: 'Fahrzeugverwaltung: Verwalten Sie alle Fahrzeuge und sehen Sie deren Diagnosehistorie auf einen Blick'
      },
      {
        url: '/screenshots/subscription-plan.png',
        caption: 'Plan-Features: Alle enthaltenen Features Ihres Plans inkl. Dual-AI-System und erweiterte Funktionen'
      }
    ]
  },

  'lightning-fast': {
    icon: <Zap className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Blitzschnelle Diagnose',
    subtitle: 'Von Symptom zur Lösung in unter 60 Sekunden',
    description: 'Dank unserer optimierten KI-Architektur und intelligenten Caching-Strategien erhalten Sie präzise Diagnosen in Rekordzeit – keine stundenlange Recherche mehr nötig.',
    keyFeatures: [
      {
        title: '<60 Sekunden Response',
        description: 'Durchschnittliche Diagnosezeit unter einer Minute'
      },
      {
        title: 'Intelligentes Caching',
        description: 'Häufige Probleme werden noch schneller beantwortet'
      },
      {
        title: 'Echtzeit-Updates',
        description: 'Sehen Sie den Diagnoseprozess in Echtzeit'
      },
      {
        title: 'Parallele Verarbeitung',
        description: 'Beide KI-Systeme arbeiten gleichzeitig'
      }
    ],
    benefits: [
      '10x schneller als traditionelle Werkstattdiagnose',
      'Sofortige Problemidentifikation',
      'Weniger Standzeit',
      'Schnellere Reparaturentscheidungen'
    ],
    screenshots: [
      {
        url: '/screenshots/dashboard.png',
        caption: 'Schnellzugriff: Starten Sie eine neue Diagnose direkt vom Dashboard in Sekunden'
      },
      {
        url: '/screenshots/history-sessions.png',
        caption: 'Blitzschnelle Ergebnisse: Alle Diagnosen erscheinen in unter 60 Sekunden im Verlauf'
      }
    ]
  },

  'web-research': {
    icon: <Globe className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Live Web-Recherche',
    subtitle: 'Zugriff auf aktuelle technische Bulletins und Expertenforen',
    description: 'Die KI durchsucht in Echtzeit technische Bulletins, Rückrufaktionen, Expertenforen und Herstellerdatenbanken, um die aktuellsten Informationen zu Ihrem Problem zu finden.',
    keyFeatures: [
      {
        title: 'Echtzeit-Datenbank-Zugriff',
        description: 'Zugriff auf über 50 Millionen technische Dokumente'
      },
      {
        title: 'Rückrufaktionen-Check',
        description: 'Automatische Prüfung auf bekannte Probleme und Rückrufe'
      },
      {
        title: 'Expertenforen-Integration',
        description: 'Findet bewährte Lösungen aus der Community'
      },
      {
        title: 'Immer up-to-date',
        description: 'Keine veralteten Informationen – alles aktuell'
      }
    ],
    benefits: [
      'Zugang zu Expertenwissen weltweit',
      'Aktuelle Problemlösungen',
      'Bekannte Schwachstellen identifizieren',
      'Fundierte Entscheidungen treffen'
    ],
    screenshots: []
  },

  'pdf-reports': {
    icon: <FileText className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Professionelle PDF-Berichte',
    subtitle: 'Detaillierte Berichte mit Zeitaufwand pro Schritt',
    description: 'Erhalten Sie professionell formatierte PDF-Berichte mit Ihrem Logo, detaillierten Diagnose-Schritten, Zeitangaben und allen relevanten Informationen – perfekt für Kundenrechnungen und Dokumentation.',
    keyFeatures: [
      {
        title: 'Custom Branding',
        description: 'Ihr Logo und Firmendesign auf jedem Bericht'
      },
      {
        title: 'Detaillierte Zeiterfassung',
        description: 'Exakte Zeitangaben für jeden Diagnoseschritt'
      },
      {
        title: 'Schritt-für-Schritt Dokumentation',
        description: 'Vollständige Nachvollziehbarkeit des Diagnoseprozesses'
      },
      {
        title: 'Professionelles Layout',
        description: 'Übersichtlich strukturiert und kundenfreundlich'
      }
    ],
    benefits: [
      'Professioneller Außenauftritt',
      'Transparenz für Kunden',
      'Rechtssichere Dokumentation',
      'Einfache Abrechnung'
    ],
    screenshots: []
  },

  'step-instructions': {
    icon: <Target className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Schritt-für-Schritt Anleitung',
    subtitle: 'Exakte Positionen, benötigte Werkzeuge und Sicherheitshinweise',
    description: 'Jeder Diagnoseschritt enthält präzise Angaben zur Bauteilposition, benötigte Werkzeuge, Sicherheitshinweise und detaillierte Anweisungen – alles was Sie für eine erfolgreiche Diagnose brauchen.',
    keyFeatures: [
      {
        title: 'Präzise Lokalisierung',
        description: 'Genaue Beschreibung wo sich das Bauteil befindet'
      },
      {
        title: 'Werkzeug-Liste',
        description: 'Alle benötigten Werkzeuge vor Beginn bekannt'
      },
      {
        title: 'Sicherheitshinweise',
        description: 'Wichtige Warnungen vor potenziellen Gefahren'
      },
      {
        title: 'Verständliche Sprache',
        description: 'Technische Begriffe werden erklärt'
      }
    ],
    benefits: [
      'Keine Überraschungen bei der Arbeit',
      'Effiziente Vorbereitung',
      'Sicheres Arbeiten',
      'Weniger Fehler'
    ],
    screenshots: []
  }
};

// FMS Features
export const fmsFeatures = {
  'gps-tracking': {
    icon: <MapPin className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Live GPS-Tracking',
    subtitle: 'Echtzeit-Überwachung Ihrer gesamten Flotte',
    description: 'Behalten Sie Ihre gesamte Flotte in Echtzeit im Blick. Sehen Sie genau wo sich jedes Fahrzeug befindet, verfolgen Sie Routen und optimieren Sie Ihre Logistik.',
    keyFeatures: [
      {
        title: 'Echtzeit-Standorte',
        description: 'Live-Position aller Fahrzeuge auf einer Karte'
      },
      {
        title: 'Route History',
        description: 'Vollständige Fahrtenhistorie der letzten 90 Tage'
      },
      {
        title: 'Geo-Fencing',
        description: 'Alarme bei Verlassen definierter Bereiche'
      },
      {
        title: 'Google Maps Integration',
        description: 'Verkehrslagen und optimierte Routenplanung'
      }
    ],
    benefits: [
      'Bessere Disposition',
      'Effizientere Routen',
      'Diebstahlschutz',
      'Kundenservice verbessern'
    ],
    screenshots: [
      {
        url: '/screenshots/dashboard.png',
        caption: 'Zentrale Übersicht: Dashboard zeigt alle wichtigen Metriken und Fahrzeugstatus auf einen Blick'
      },
      {
        url: '/screenshots/vehicles-list.png',
        caption: 'Flottenübersicht: Alle Fahrzeuge mit Status, letzten Aktivitäten und Schnellzugriff'
      }
    ]
  },

  'fleet-management': {
    icon: <Truck className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Fahrzeugverwaltung',
    subtitle: 'Zentrale Verwaltung aller Fahrzeugdaten',
    description: 'Verwalten Sie alle Fahrzeugdaten zentral an einem Ort: Zulassungen, Versicherungen, Wartungsintervalle, Fahrzeugpapiere und mehr – alles digital und jederzeit verfügbar.',
    keyFeatures: [
      {
        title: 'Digitale Fahrzeugakte',
        description: 'Alle Dokumente und Daten an einem Ort'
      },
      {
        title: 'Wartungsplanung',
        description: 'Automatische Erinnerungen für Inspektionen'
      },
      {
        title: 'Dokumenten-Management',
        description: 'Upload und Verwaltung aller Fahrzeugpapiere'
      },
      {
        title: 'Kostenübersicht',
        description: 'Alle Kosten pro Fahrzeug auf einen Blick'
      }
    ],
    benefits: [
      'Kein Papierkram mehr',
      'Keine vergessenen Termine',
      'Vollständige Übersicht',
      'Compliance sicherstellen'
    ],
    screenshots: [
      {
        url: '/screenshots/vehicles-list.png',
        caption: 'Digitale Fahrzeugverwaltung: Alle Fahrzeugdaten, Historie und Status zentral verwaltet'
      },
      {
        url: '/screenshots/history-sessions.png',
        caption: 'Vollständige Historie: Alle Diagnosen, Wartungen und Aktivitäten dokumentiert'
      }
    ]
  },

  'obd-diagnostics': {
    icon: <Activity className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'OBD Live-Diagnose',
    subtitle: 'Remote-Diagnose mit Vgate OBD2 Dongle',
    description: 'Führen Sie Fahrzeugdiagnosen remote durch! Mit dem Vgate OBD2 Bluetooth-Dongle und unserem Desktop-Agent können Sie Fehler codes auslesen, Live-Daten überwachen und sogar Kilometerstand-Manipulationen erkennen – alles aus der Ferne.',
    keyFeatures: [
      {
        title: 'Remote Fehlercode-Auslesen',
        description: 'DTCs auslesen und löschen ohne vor Ort zu sein'
      },
      {
        title: 'Live-Daten Monitor',
        description: 'Motorwerte, Temperaturen und mehr in Echtzeit'
      },
      {
        title: 'Fahrzeugcheck',
        description: 'Automatischer Gesundheitscheck des Fahrzeugs'
      },
      {
        title: 'Km-Manipulations-Erkennung',
        description: 'Erkennt Anzeichen von Tachomanipulationen'
      }
    ],
    howItWorks: [
      {
        title: 'Vgate Dongle verbinden',
        description: 'Stecken Sie den Vgate OBD2 Dongle in die OBD-Buchse des Fahrzeugs'
      },
      {
        title: 'Desktop Agent starten',
        description: 'Installieren Sie den CarCortex Desktop Agent auf Ihrem Windows-PC'
      },
      {
        title: 'Automatische Verbindung',
        description: 'Der Agent verbindet sich automatisch via Bluetooth'
      },
      {
        title: 'Remote-Diagnose starten',
        description: 'Führen Sie Diagnosen aus der Ferne über Ihr Dashboard durch'
      }
    ],
    benefits: [
      'Zeit sparen durch Remote-Diagnose',
      'Kosten sparen bei Werkstattbesuchen',
      'Proaktive Wartung',
      'Manipulation erkennen'
    ],
    screenshots: [
      {
        url: '/screenshots/dashboard.png',
        caption: 'OBD Live Dashboard: Zukünftig werden hier Live-Daten vom Fahrzeug angezeigt (In Entwicklung)'
      },
      {
        url: '/screenshots/subscription-plan.png',
        caption: 'Premium-Feature: OBD Live-Diagnose wird in ausgewählten Plänen verfügbar sein'
      }
    ]
  },

  'driver-management': {
    icon: <Shield className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Fahrerverwaltung',
    subtitle: 'Digitale Führerscheinkontrolle und Fahrtenbuch',
    description: 'Verwalten Sie alle Fahrerdaten digital: Führerscheine, Qualifikationen, Schulungen und mehr. Mit automatischer Führerscheinkontrolle und digitalem Fahrtenbuch.',
    keyFeatures: [
      {
        title: 'Digitale Führerscheine',
        description: 'Upload und Verwaltung aller Führerscheine'
      },
      {
        title: 'Automatische Ablauf-Erinnerungen',
        description: 'Nie wieder einen abgelaufenen Führerschein übersehen'
      },
      {
        title: 'Fahrer-Scoring',
        description: 'Bewertung des Fahrverhaltens und Effizienz'
      },
      {
        title: 'Digitales Fahrtenbuch',
        description: 'Automatische Erfassung aller Fahrten'
      }
    ],
    benefits: [
      'Rechtssichere Dokumentation',
      'Compliance gewährleisten',
      'Fahrverhalten verbessern',
      'Automatisierung'
    ],
    screenshots: []
  },

  'analytics': {
    icon: <BarChart3 className="w-12 h-12" style={{ color: '#7CD4FD' }} />,
    title: 'Analytics & Reports',
    subtitle: 'Detaillierte Auswertungen und Kostenkontrolle',
    description: 'Erhalten Sie detaillierte Einblicke in Ihre Flottenkosten, Kraftstoffverbrauch, Auslastung und mehr. Mit customizable Reports und Export-Funktionen.',
    keyFeatures: [
      {
        title: 'Kosten-Tracking',
        description: 'Alle Kosten pro Fahrzeug und Kategorie'
      },
      {
        title: 'Kraftstoff-Analyse',
        description: 'Verbrauchsübersicht und Einsparpotenziale'
      },
      {
        title: 'Auslastungs-Reports',
        description: 'Wie effizient wird Ihre Flotte genutzt?'
      },
      {
        title: 'Custom Reports',
        description: 'Erstellen Sie individuelle Auswertungen'
      }
    ],
    benefits: [
      'Kosten senken durch Transparenz',
      'Ineffizienzen identifizieren',
      'Fundierte Entscheidungen treffen',
      'ROI nachweisen'
    ],
    screenshots: []
  }
};
